/* eslint-disable no-param-reassign */
/* eslint-disable prefer-template */

import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";

import { envConf } from "../Config";

import { ApiUrl } from "./ApiUrl";
import { getToLocalStorage } from "../Library/Utils";
import { isUserShareholder, isUserSuccessManager } from "../Services/Permissions";

const { apiBaseUrl, envType } = envConf;

const SLOW_REQUEST_MS = 1000;
const isKpiRequest = (url?: string) => !!url && /(^|\/)kpi\//.test(url);
const newKpiTraceId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * KPI traces are automatic in local and dev01 builds. Elsewhere QA can opt in
 * with `localStorage.apiTiming = "1"` and a reload.
 */
const apiTimingEnabled = () => {
  if (process.env.NODE_ENV !== "production") return true;
  if (envType.toLowerCase() === "development") return true;
  try {
    return localStorage.getItem("apiTiming") === "1";
  } catch {
    return false;
  }
};

const summarizeKpiPayload = (data: any) => {
  if (!data || typeof data !== "object" || data instanceof FormData) return undefined;
  const result: Record<string, unknown> = {};
  [
    "name",
    "unit_type",
    "frequency",
    "plan_frequency",
    "logic_level",
    "value_type",
    "year",
  ].forEach((key) => {
    if (data[key] !== undefined) result[key] = data[key];
  });
  ["kpi_frequencies", "absolute_kpis"].forEach((key) => {
    if (Array.isArray(data[key])) result[`${key}_count`] = data[key].length;
  });
  return Object.keys(result).length ? result : undefined;
};

/**
 * Converts an object of key-value pairs into a URL query string.
 *
 * Example:
 *   makeQueryParams({ page: 1, pageSize: 10 }) 
 *   => "?page=1&pageSize=10"
 *
 * @param obj - An object with string keys and values that can be serialized (string, number, boolean).
 * @returns A URL query string starting with "?".
 */
export const makeQueryParams = (
  obj: Record<string, string | number | boolean | null>
) => {

  const toSnake = (str: string) =>
    str.replace(/([A-Z])/g, "_$1").toLowerCase();

  return (
    "?" +
    Object.keys(obj)
      .map((key) => {
        const snakeKey = toSnake(key);
        // eslint-disable-next-line security/detect-object-injection
        return `${encodeURIComponent(snakeKey)}=${encodeURIComponent(
          String(obj[key])
        )}`;
      })
      .join("&")
  );
};

export const authApi = axios.create({
  baseURL: `${apiBaseUrl}`,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    language: getToLocalStorage("language")
      ? getToLocalStorage("language")
      : "De"},
  withCredentials: true,
  responseType: "json"});

const api = axios.create({
  baseURL: `${apiBaseUrl}`,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json"},
  withCredentials: true,
  responseType: "json"});

let sessionExpired = false;

/**
 * True once a 401 has cleared the stored session and the browser is on its way
 * to /signin. Anything that renders in that window (the ErrorBoundary) should
 * stay quiet instead of reporting the half-torn-down page as a crash.
 */
export const isSessionExpired = () => sessionExpired;

/**
 * The access cookie lasts minutes; the refresh cookie buys a new pair. Shared
 * while in flight, so a page whose requests all hit the expiry at once renews
 * once and retries them all. Resolves false when the session has really ended
 * (logged out, password reset, idle too long).
 */
let renewal: Promise<boolean> | null = null;
const renewSession = () => {
  if (!renewal) {
    renewal = authApi
      .post(ApiUrl.authModule.user_refresh_token)
      .then(
        () => true,
        () => false
      )
      .finally(() => {
        renewal = null;
      });
  }
  return renewal;
};

type RetriableConfig = AxiosRequestConfig & { retriedAfterRenewal?: boolean };

api.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    // Stamp start time for duration tracking
    (config as any).requestStartTime = Date.now();

    if (isKpiRequest(config.url)) {
      const traceId = newKpiTraceId();
      (config as any).kpiTraceId = traceId;
      config.headers = config.headers ?? {};
      config.headers["X-KPI-Trace-ID"] = traceId;
      if (apiTimingEnabled()) {
        console.info(
          `[KPI TRACE] START id=${traceId} ${config.method?.toUpperCase()} ${config.url}`,
          summarizeKpiPayload(config.data)
        );
      }
    }



    config.headers = config.headers ?? {};
    config.params = config.params || {};
    const language = getToLocalStorage("language")
      ? getToLocalStorage("language")
      : "De";
    config.headers.language = language;
    const user = getToLocalStorage("user");
    config.headers.company_id = isUserSuccessManager(user)
      ? user?.company_id || ""
      : "";

    if (isUserSuccessManager(user) || isUserShareholder(user)) {
      config.params = {
        ...config.params,
        company_id: user?.company_id || ""};
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Per-request timing in the console, to see which calls a slow flow spends its
 * time on (e.g. POST /kpi/ followed by the list refetch). On in development
 * builds; elsewhere set localStorage.apiTiming = "1" and reload.
 */
const logRequestTiming = (response: AxiosResponse) => {
  if (!apiTimingEnabled()) return;
  const { config } = response;
  const startedAt = (config as any)?.requestStartTime;
  if (!startedAt) return;
  const duration = Date.now() - startedAt;
  const traceId = response.headers?.["x-kpi-trace-id"] || (config as any).kpiTraceId;
  const kpiDetails = traceId
    ? ` id=${traceId} server=${response.headers?.["x-kpi-server-ms"] ?? "?"}ms db=${response.headers?.["x-kpi-db-ms"] ?? "?"}ms queries=${response.headers?.["x-kpi-query-count"] ?? "?"}`
    : "";
  const prefix = traceId ? "[KPI TRACE] SUCCESS" : "[API]";
  const line = `${prefix}${kpiDetails} ${config.method?.toUpperCase()} ${config.url} -> ${response.status} client=${duration}ms`;
  if (duration >= SLOW_REQUEST_MS) {
    console.warn(line);
  } else {
    console.log(line);
  }
};

api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    logRequestTiming(response);
    return response;
  },
  async (error: AxiosError): Promise<any> => {
    const status = error?.response?.status;
    const retriable = error?.config as RetriableConfig | undefined;
    if (status === 401 && retriable && !retriable.retriedAfterRenewal && !sessionExpired) {
      if (await renewSession()) {
        retriable.retriedAfterRenewal = true;
        return api(retriable);
      }
    }

    const duration = Date.now() - ((error?.config as any)?.requestStartTime ?? Date.now());
    const url = error?.config?.url;
    const responseData = error?.response?.data as any;
    const traceId =
      error?.response?.headers?.["x-kpi-trace-id"] ||
      (error?.config as any)?.kpiTraceId;

    if (traceId && apiTimingEnabled()) {
      console.error(
        `[KPI TRACE] ERROR id=${traceId} ${error?.config?.method?.toUpperCase()} ${url} -> ${status ?? "NETWORK"} client=${duration}ms server=${error?.response?.headers?.["x-kpi-server-ms"] ?? "?"}ms db=${error?.response?.headers?.["x-kpi-db-ms"] ?? "?"}ms queries=${error?.response?.headers?.["x-kpi-query-count"] ?? "?"}`,
        {
          message: responseData?.message || error.message,
          errors: responseData?.errors,
        }
      );
    }

    console.group(`[API Error] <- ${status} ${url} (${duration}ms)`);
    console.error("Status:", status);
    console.error("URL:", url);
    console.error("Response:", responseData);
    if (responseData?.errors) {
      console.error("Errors:", responseData.errors);
    }
    if (responseData?.message) {
      console.error("Message:", responseData.message);
    }
    console.groupEnd();

    if (status === 401) {
      if (!sessionExpired) {
        console.warn("[AUTH] 401 Unauthorized caught. Clearing local storage and redirecting.");
        // Preserve the user's chosen language before clearing auth state so it
        // survives the token-expiry redirect back to the sign-in page.
        const preservedLanguage = localStorage.getItem("language");
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("user");
        if (preservedLanguage) {
          localStorage.setItem("language", preservedLanguage);
        }
        // Only flag the session once a full page load is actually under way;
        // that load resets this module, so the flag never outlives it.
        if (window.location.pathname !== "/signin") {
          sessionExpired = true;
          window.location.replace(`${window.location.origin}/signin`);
        }
      }
      // A tab restored hours later (session cookie gone, localStorage still
      // "authenticated") fires a burst of requests that all 401. Rejecting
      // them runs every caller's catch/finally while the sign-in page is still
      // loading: error toasts, state updates, re-renders without the stored
      // user, and the ErrorBoundary's "Something went wrong" screen. Leave
      // them pending instead; the navigation discards the page.
      if (sessionExpired) {
        return new Promise<AxiosError>(() => {});
      }
    }
    return Promise.reject(error);
  }
);

export default api;
